import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, FileText } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { LoadError } from '../components/layout/LoadError';
import { EmptyState } from '../components/ui/EmptyState';
import { useMyPolicies, type PolicyKind } from '../hooks';

const KIND: Record<PolicyKind, string> = {
  work_rule: 'ข้อบังคับเกี่ยวกับการทำงาน',
  regulation: 'ระเบียบ / คำสั่ง',
  announcement: 'ประกาศ',
};

function thaiDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00+07:00`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('th-TH', {
    timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function MyPoliciesSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-label="กำลังโหลดเอกสาร">
      {[0, 1, 2].map((key) => (
        <div key={key} className="card p-4">
          <div className="h-4 w-3/4 bg-gray-100 rounded" />
          <div className="h-3 w-1/2 bg-gray-100 rounded mt-2" />
          <div className="h-3 w-full bg-gray-100 rounded mt-3" />
        </div>
      ))}
    </div>
  );
}

export function MyPolicies() {
  const { data, isLoading, error, refetch } = useMyPolicies();

  return (
    <PageLayout title="ข้อบังคับและประกาศ">
      <div className="space-y-4 pb-8">
        {isLoading ? (
          <MyPoliciesSkeleton />
        ) : error || !data ? (
          <LoadError error={error} onRetry={() => void refetch()} />
        ) : data.policies.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="ยังไม่มีเอกสารที่ต้องอ่าน"
            detail="เมื่อฝ่ายบุคคลเผยแพร่ข้อบังคับ ระเบียบ หรือประกาศที่ถึงคุณ เอกสารจะแสดงที่นี่"
          />
        ) : (
          <>
            {data.pending_count > 0 && (
              <div className="card p-4 border-l-4 border-l-amber-400">
                <p className="font-semibold">มีเอกสารค้างรับทราบ {data.pending_count} ฉบับ</p>
                <p className="text-sm text-gray-600 mt-1">เปิดอ่านฉบับที่ค้างให้ถึงท้ายเอกสาร แล้วกดรับทราบ</p>
              </div>
            )}
            <div className="space-y-3">
              {data.policies.map((policy) => (
                <Link
                  key={policy.id}
                  to={`/policies/${encodeURIComponent(policy.id)}`}
                  className={`card p-4 flex items-start gap-3 min-h-24 transition hover:bg-gray-50 ${
                    policy.acked ? '' : 'border-l-4 border-l-amber-400'
                  }`}
                >
                  <FileText className={`w-6 h-6 shrink-0 mt-0.5 ${policy.acked ? 'text-gray-400' : 'text-brand-600'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-semibold break-words">{policy.title}</h2>
                      {policy.acked && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {policy.ack_required ? 'รับทราบแล้ว' : 'เอกสารเพื่อทราบ'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {KIND[policy.kind]} · เลขที่ {policy.doc_no} · ฉบับที่ {policy.version}
                    </p>
                    <p className="text-xs text-gray-500">มีผล {thaiDate(policy.effective_from)}</p>
                    {policy.summary && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{policy.summary}</p>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 shrink-0 mt-1" />
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
